import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useCustomer } from "../util/CustomerContext";
import { getApi, postApi, uploadInvoiceImageApi } from "../util/helper";
import Notification from "./shared/Notification";

function Record() {
  const { id } = useParams();
  const { customer } = useCustomer();
  console.log(customer, "customer", id);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("calender");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDays, setSelectedDays] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [amount, setAmount] = useState("");
  const fileInputRef = useRef(null);
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  // State for range selection
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const showNotification = useCallback((type, message) => {
    setNotification({ show: true, type, message });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, type: "", message: "" });
    }, 3000);
  }, []);

  // Function to select a range of days
  const selectRange = () => {
    if (!rangeStart || !rangeEnd) {
      showNotification("error", "Please select both start and end dates");
      return;
    }

    const startDate = new Date(rangeStart);
    const endDate = new Date(rangeEnd);

    if (startDate > endDate) {
      showNotification("error", "Start date must be before end date");
      return;
    }

    // Calculate the difference in days
    const timeDifference = endDate.getTime() - startDate.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

    if (daysDifference > 31) {
      showNotification("error", "Cannot select more than 31 days at once");
      return;
    }

    // Get the current month key
    const monthKey = getMonthKey(startDate.getFullYear(), startDate.getMonth());

    // Create an array to store the selected days
    const newSelectedDays = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      if (currentDate.getMonth() === startDate.getMonth()) {
        newSelectedDays.push({
          day: currentDate.getDate(),
          approval: "pending",
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Update the selected days state
    setSelectedDays((prev) => ({
      ...prev,
      [monthKey]: newSelectedDays,
    }));

    showNotification(
      "success",
      `Selected ${newSelectedDays.length} days in the range`,
    );
  };

  // Function to clear all selections
  const clearSelection = () => {
    setSelectedDays({});
    setRangeStart("");
    setRangeEnd("");
    showNotification("info", "All selections cleared");
  };

  useEffect(() => {
    setLoading(true);
    getApi(
      `/invoice/service/${id}/customer/${customer._id}`,
      (resp) => {
        console.log(resp.data.invoices, "invoice");
        // setServiceInvoice(resp.data.invoices);
        setLoading(false);
      },
      (err) => {
        console.log(err, "error getting invoice");
        setLoading(false);
      },
    );
  }, [id, customer._id]);

  useEffect(() => {
    getApi(
      `/customer-record/customer/${customer._id}`,
      (resp) => {
        if (resp.data.data && resp.data.data.length > 0) {
          const processedSelectedDays = {};

          resp.data.data.forEach((record) => {
            if (record.services && record.services.length > 0) {
              record.services.forEach((service) => {
                // Use loose equality or string conversion to handle potential type mismatches
                if (String(service.serviceId) === String(id)) {
                  // Match current service
                  // Process the days object which contains years and months
                  Object.entries(service.days || {}).forEach(
                    ([year, months]) => {
                      Object.entries(months).forEach(([month, days]) => {
                        const monthKey = `${year}-${month}`;
                        if (!processedSelectedDays[monthKey]) {
                          processedSelectedDays[monthKey] = [];
                        }

                        if (Array.isArray(days)) {
                          days.forEach((dayObj) => {
                            processedSelectedDays[monthKey].push({
                              day: dayObj.day,
                              approval: dayObj.approval || "pending",
                            });
                          });
                        } else {
                          console.warn("Days is not an array:", days); // Warning log
                        }
                      });
                    },
                  );
                }
              });
            }
          });
          setSelectedDays(processedSelectedDays);
        } else {
          console.log("No records found or empty data"); // Debug log
        }
      },
      (err) => {
        console.log(err, "error getting records");
      },
    );
  }, [id, customer._id]);
  // Month navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  // Generate days for the current month
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth(); // 0-indexed (0 = January)
  // const daysInMonth = new Date(year, month + 1, 0).getDate()
  const daysInMonth = 31; // Always show 31 days regardless of actual month
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Helper function to get month key
  const getMonthKey = (year, monthIndex) =>
    `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

  // Handle day selection - only allows pending status
  const handleDayClick = (day) => {
    const monthKey = getMonthKey(year, month);
    const currentMonthDays = selectedDays[monthKey] || [];

    // Find if day already exists
    const dayIndex = currentMonthDays.findIndex((d) => d.day === day);

    if (dayIndex !== -1) {
      // Day exists, remove it (toggle off)
      const updatedDays = currentMonthDays.filter((d) => d.day !== day);
      setSelectedDays((prev) => {
        const newState = { ...prev };
        if (updatedDays.length > 0) {
          newState[monthKey] = updatedDays;
        } else {
          delete newState[monthKey]; // Remove the key if no days left
        }
        return newState;
      });
    } else {
      // Add day to current month with pending approval status only
      const dayObject = { day, approval: "pending" };
      const updatedDays = [...currentMonthDays, dayObject];
      setSelectedDays((prev) => ({
        ...prev,
        [monthKey]: updatedDays,
      }));
    }
  };

  useEffect(() => {
    // In a real app, you would fetch the service details from your API
    // For now, we'll simulate this with the customer's service data
    if (customer && customer.services) {
      const service = customer.services.find((s) => s.serviceId === id);
      if (service) {
        setServiceDetails(service);
      } else {
        setError("Service not found");
      }
    }
    setLoading(false);
  }, [id, customer]);

  // Function to handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match("image.*")) {
      showNotification("error", "Please select an image file");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification("error", "File size exceeds 10MB limit");
      return;
    }

    const formData = new FormData();
    formData.append("images", file);

    setUploading(true);

    uploadInvoiceImageApi(
      "/image",
      formData,
      (response) => {
        console.log("Image uploaded successfully:");

        const imageUrl =
          response.data.urls ||
          response.data.imageUrls ||
          response.data.image_urls;

        if (imageUrl) {
          setUploadedImageUrl(imageUrl);
          showNotification("success", "Image uploaded successfully!");
        } else {
          console.error("Image URL not found in response:", response);
          showNotification("error", "Failed to get image URL from server");
        }

        setUploading(false);
      },
      (error) => {
        console.error("Error uploading image:", error);
        showNotification("error", "Error uploading image. Please try again.");
        setUploading(false);
      },
      () => {
        showNotification("error", "Received empty response from server");
        setUploading(false);
      },
    );
  };

  // Function to submit the invoice with image URL and service details
  const handleSubmitInvoice = () => {
    if (!uploadedImageUrl) {
      showNotification("error", "Please upload an image first");
      return;
    }

    if (!customer || !serviceDetails) {
      showNotification("error", "Customer or service details not available");
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      showNotification("error", "Please enter a valid amount");
      return;
    }

    // Prepare the invoice data to send to the backend
    const invoiceData = {
      customerId: customer._id,
      serviceId: serviceDetails.serviceId,
      customerName: customer.name,
      serviceName: serviceDetails.service,
      imageUrl: uploadedImageUrl[0],
      time: new Date().toISOString().split("T")[0],
      amount: parseFloat(amount),
    };

    postApi(
      "/invoice",
      invoiceData,
      (response) => {
        // console.log("Invoice submitted successfully:", response);
        showNotification("success", "Invoice submitted successfully!");
        setUploadedImageUrl(null);
        setAmount(""); // Reset the amount field after successful submission
      },
      (error) => {
        console.error("Error submitting invoice:", error);
        showNotification(
          "error",
          "Error submitting invoice. Please try again.",
        );
      },
    );
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex justify-center items-center h-screen"
      >
        <div className="text-xl">Loading records...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-center items-center h-screen"
      >
        <div className="text-xl text-red-600">{error}</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="p-6 min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 py-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="max-w-4xl mx-auto"
      >
        <Notification
          show={notification.show}
          type={notification.type}
          message={notification.message}
        />

        <div className="flex justify-between items-center mb-6">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            onClick={() => window.history.back()}
          >
            ← Back
          </button>
          <h1 className="text-3xl font-bold text-center text-gray-800 flex-grow ml-8">
            Service Records
          </h1>
        </div>

        {serviceDetails ? (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white p-6 rounded-lg shadow-md mb-8"
          >
            <h2 className="text-2xl font-semibold mb-4 text-blue-700">
              {serviceDetails.service}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded text-[12px] sm:text-[14px]">
                <h3 className="font-semibold text-gray-700">Service ID</h3>
                <p className="text-gray-900">{serviceDetails.serviceId}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded text-[12px] sm:text-[14px]">
                <h3 className="font-semibold text-gray-700">Duration</h3>
                <p className="text-gray-900">{serviceDetails.duration}</p>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center py-10"
          >
            <p className="text-xl text-gray-600">
              No records found for this service.
            </p>
          </motion.div>
        )}

        <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
          <button
            className={`py-3 px-5 font-medium text-sm rounded-full transition-all duration-300 ${
              activeTab === "calender"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setActiveTab("calender")}
          >
            Calender
          </button>
          <button
            className={`py-3 px-5 font-medium text-sm rounded-full transition-all duration-300 ${
              activeTab === "invoice"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setActiveTab("invoice")}
          >
            Invoice
          </button>
          <button
            className={`py-3 px-5 font-medium text-sm rounded-full transition-all duration-300 ${
              activeTab === "view-invoice"
                ? "bg-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
            onClick={() => setActiveTab("view-invoice")}
          >
            view Invoice
          </button>
        </div>

        {/* Calendar Section */}
        {activeTab === "calender" && (
          <>
            <motion.div
              className="bg-white rounded-2xl shadow-lg p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                Select Appointment Days
              </h2>

              {/* Legend for approval status colors */}
              <div className="flex flex-wrap justify-center gap-4 mb-6">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-yellow-400 rounded mr-2"></div>
                  <span className="text-sm">Pending</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-indigo-500 rounded mr-2"></div>
                  <span className="text-sm">Checked</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                  <span className="text-sm">Approved</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                  <span className="text-sm">Rejected</span>
                </div>
              </div>

              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={goToPreviousMonth}
                  className={`px-4 py-2  ${
                    serviceDetails.allowBackDate
                      ? "text-indigo-700 hover:bg-indigo-200 bg-indigo-100"
                      : "text-gray-700 hover:bg-gray-200 bg-gray-100"
                  }  rounded-lg  transition-colors`}
                  disabled={!serviceDetails.allowBackDate}
                >
                  &lt; Prev
                </button>

                <h3 className="text-xl font-semibold text-gray-800">
                  {currentDate.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </h3>

                <button
                  onClick={goToNextMonth}
                  className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                >
                  Next &gt;
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2 mb-4">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div
                      key={day}
                      className="text-center font-medium text-gray-600 py-2"
                    >
                      {day}
                    </div>
                  ),
                )}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {/* Empty cells for the first week */}
                {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                  <div key={`empty-${index}`} className="h-12"></div>
                ))}

                {/* Day cells */}
                {Array.from({ length: daysInMonth }).map((_, index) => {
                  const day = index + 1;
                  const monthKey = getMonthKey(year, month);
                  const currentMonthDays = selectedDays[monthKey] || [];
                  const dayInfo = currentMonthDays.find((d) => d.day === day);

                  let bgColor = "bg-gray-100"; // Default color for unselected days
                  let textColor = "text-gray-700";

                  if (dayInfo) {
                    // Apply color based on approval status
                    switch (dayInfo.approval) {
                      case "approved":
                        bgColor = "bg-green-500";
                        textColor = "text-white";
                        break;
                      case "rejected":
                        bgColor = "bg-red-500";
                        textColor = "text-white";
                        break;
                      case "checked":
                        bgColor = "bg-indigo-500";
                        textColor = "text-white";
                        break;
                      case "pending":
                      default:
                        bgColor = "bg-yellow-400";
                        textColor = "text-white";
                        break;
                    }
                  }

                  return (
                    <motion.button
                      key={day}
                      className={`h-12 rounded-lg flex items-center justify-center text-base font-medium transition-all duration-200 ${bgColor} ${textColor} hover:opacity-90 shadow`}
                      onClick={() => handleDayClick(day)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {dayInfo ? <span className="font-bold">{day}</span> : day}
                    </motion.button>
                  );
                })}
              </div>

              {/* <div className="mt-6 text-center text-sm text-gray-600">
                Selected days:{" "}
                {Object.keys(selectedDays).length > 0
                  ? Object.entries(selectedDays)
                      .map(
                        ([month, days]) =>
                          `${month}: [${days
                            .map((d) => `${d.day}(${d.approval})`)
                            .join(", ")}]`,
                      )
                      .join(", ")
                  : "None"}
              </div> */}
            </motion.div>

            {/* Range Selection Controls */}
            <div className="mt-6 flex flex-wrap gap-4 justify-center">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  From:
                </label>
                <input
                  type="date"
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">To:</label>
                <input
                  type="date"
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                />
              </div>

              <button
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                onClick={selectRange}
              >
                Select Range
              </button>

              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-sm"
                onClick={clearSelection}
              >
                Clear All
              </button>
            </div>

            <div className="flex justify-center gap-4 mt-6">
              {/* <motion.button
                className="px-6 py-3 rounded-full text-lg font-bold text-white bg-gray-500 hover:bg-gray-600 shadow-lg"
                onClick={() => window.history.back()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Back
              </motion.button> */}

              <motion.button
                className={`px-8 py-4 rounded-full text-lg font-bold text-white shadow-lg ${
                  Object.keys(selectedDays).some(
                    (month) => selectedDays[month].length > 0,
                  )
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
                disabled={
                  !Object.keys(selectedDays).some(
                    (month) => selectedDays[month].length > 0,
                  )
                }
                onClick={() => {
                  // Prepare data arrays
                  // const customerData = customer ? [customer] : [];
                  const serviceData = serviceDetails ? [serviceDetails] : [];

                  const daysByYearAndMonth = {};
                  Object.entries(selectedDays).forEach(([key, days]) => {
                    const [year, month] = key.split("-");
                    if (!daysByYearAndMonth[year]) {
                      daysByYearAndMonth[year] = {};
                    }
                    daysByYearAndMonth[year][month] = days;
                  });

                  // Create the recordData object according to the schema
                  const recordData = {
                    name: customer?.name || "",
                    customerId: customer?._id || "",
                    phone: customer?.phone || "",
                    address: customer?.address || "",
                    services: [
                      {
                        title: serviceData[0]?.service || "",
                        serviceId: serviceData[0]?.serviceId || "",
                        days: daysByYearAndMonth,
                      },
                    ],
                  };
                  console.log(recordData, "record inside");
                  postApi(
                    "/customer-record",
                    recordData,
                    (resp) => {
                      console.log("response from record");
                      showNotification(
                        "success",
                        "Appointment submitted successfully!",
                      );
                    },
                    (err) => {
                      console.log(err, "error posting record");
                      showNotification(
                        "error",
                        "Error submitting appointment. Please try again.",
                      );
                    },
                  );
                }}
                whileHover={
                  Object.keys(selectedDays).some(
                    (month) => selectedDays[month].length > 0,
                  )
                    ? { scale: 1.05 }
                    : {}
                }
                whileTap={
                  Object.keys(selectedDays).some(
                    (month) => selectedDays[month].length > 0,
                  )
                    ? { scale: 0.95 }
                    : {}
                }
              >
                Submit Appointment (
                {Object.values(selectedDays).reduce(
                  (total, days) => total + days.length,
                  0,
                )}{" "}
                day
                {Object.values(selectedDays).reduce(
                  (total, days) => total + days.length,
                  0,
                ) !== 1
                  ? "s"
                  : ""}
                )
              </motion.button>
            </div>
          </>
        )}
        {activeTab === "invoice" && (
          <motion.div
            className="bg-white rounded-2xl shadow-lg p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Invoice
            </h2>

            <div className="space-y-6">
              <div className="bg-indigo-50 sm:flex p-2 space-y-2 sm:gap-2">
                <div className="bg-white p-4 rounded-lg flex-1">
                  <h3 className="font-semibold text-gray-700 mb-2">
                    Customer Information
                  </h3>
                  <p className="text-gray-900">
                    <span className="font-medium">Name:</span>{" "}
                    {customer?.name || "N/A"}
                  </p>
                  <p className="text-gray-900">
                    <span className="font-medium">Service:</span>{" "}
                    {serviceDetails?.service || "N/A"}
                  </p>
                  <p className="text-gray-900">
                    <span className="font-medium">Service ID:</span>{" "}
                    {serviceDetails?.serviceId || "N/A"}
                  </p>
                </div>
                {uploadedImageUrl && (
                  <div className="flex-1 bg-white rounded-lg">
                    <img
                      src={uploadedImageUrl}
                      alt=""
                      className="w-full h-40 rounded-lg"
                    />
                  </div>
                )}
              </div>

              {uploading && (
                <motion.div
                  className="mt-4 flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="flex items-center space-x-2 text-indigo-600">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span>Uploading...</span>
                  </div>
                </motion.div>
              )}

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700">
                  Upload Invoice Documents
                </h3>

                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center transition-all hover:border-indigo-400 hover:bg-indigo-50">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />

                  <div className="flex flex-col items-center justify-center">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>

                    <p className="text-gray-600 mb-2">
                      <span className="font-medium text-indigo-600">
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </p>
                    <p className="text-sm text-gray-500">
                      JPG, PNG, GIF up to 10MB
                    </p>
                  </div>

                  <motion.button
                    type="button"
                    className={`mt-4 px-4 py-2 ${uploadedImageUrl ? "bg-gray-400 hover:bg-gray-300" : "bg-indigo-600 hover:bg-indigo-700"}  text-white rounded-lg transition-colors`}
                    onClick={() => fileInputRef.current.click()}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={uploadedImageUrl}
                  >
                    Choose File
                  </motion.button>
                </div>

                {uploadedImageUrl && (
                  <div className="space-y-2">
                    <label
                      htmlFor="amount"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Amount ($)
                    </label>
                    <input
                      type="number"
                      id="amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
                <motion.button
                  type="button"
                  className={`mt-4 w-full py-3 rounded-lg font-medium ${
                    uploadedImageUrl
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                  } transition-colors`}
                  disabled={!uploadedImageUrl}
                  onClick={handleSubmitInvoice}
                  whileHover={uploadedImageUrl ? { scale: 1.02 } : {}}
                  whileTap={uploadedImageUrl ? { scale: 0.98 } : {}}
                >
                  Submit Invoice
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
        {activeTab === "view-invoice" && (
          <ViewInvoiceComponent
            serviceDetails={serviceDetails}
            customer={customer}
          />
        )}
      </motion.div>
    </motion.div>
  );
}

// ViewInvoiceComponent to display invoices in a grid format with animations and responsiveness
const ViewInvoiceComponent = ({ serviceDetails, customer }) => {
  const { id } = useParams();
  const { customer: customerFromContext } = useCustomer();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch invoices from the API
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        getApi(
          `/invoice/service/${id}/customer/${customerFromContext._id}`,
          (resp) => {
            setInvoices(resp.data.invoices || []);
            setLoading(false);
          },
          (err) => {
            setError(err.message);
            setLoading(false);
          },
        );
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (customerFromContext && id) {
      fetchInvoices();
    }
  }, [id, customerFromContext]);

  // Status badge styling based on status
  // const getStatusBadgeClass = (status) => {
  //   switch (status?.toLowerCase()) {
  //     case "paid":
  //       return "bg-green-100 text-green-800";
  //     case "pending":
  //       return "bg-yellow-100 text-yellow-800";
  //     case "overdue":
  //       return "bg-red-100 text-red-800";
  //     default:
  //       return "bg-gray-100 text-gray-800";
  //   }
  // };

  if (loading) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-lg p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          View Invoice for {serviceDetails?.service || "Service"}
        </h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-lg p-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
          View Invoice for {serviceDetails?.service || "Service"}
        </h2>
        <div className="text-center text-red-500 py-10">
          Error loading invoices: {error}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        View Invoice for {serviceDetails?.service || "Service"}
      </h2>

      {invoices.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-gray-500">No invoices found for this service.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {invoices.map((invoice, index) => (
            <motion.div
              key={invoice._id || index}
              className="border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 bg-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="relative">
                {invoice.imageUrl ? (
                  <img
                    src={
                      Array.isArray(invoice.imageUrl)
                        ? invoice.imageUrl[0]
                        : invoice.imageUrl
                    }
                    alt={invoice.description || "Invoice document"}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">No Image</span>
                  </div>
                )}
                {/* <div className="absolute top-3 right-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(invoice.status)}`}
                  >
                    {invoice.status
                      ? invoice.status.charAt(0).toUpperCase() +
                        invoice.status.slice(1)
                      : "Pending"}
                  </span>
                </div> */}
              </div>

              <div className="p-5">
                <div className="flex justify-between items-start">
                  <span className="text-lg font-bold text-indigo-600">
                    ₦
                    {typeof invoice.amount === "number"
                      ? invoice.amount.toFixed(2)
                      : invoice.amount || "0.00"}
                  </span>
                </div>
                <h3 className="text-[12px] text-gray-900 truncate">
                  Inv no:{" "}
                  {invoice.invoiceNumber ||
                    `INV-${invoice._id?.substring(0, 6) || index}`}
                </h3>
                <p className="text-gray-600 mt-2 text-sm">
                  {invoice.description ||
                    invoice.serviceName ||
                    "Service invoice"}
                </p>

                <div className="mt-4 flex justify-between items-center">
                  <span className="text-sm text-gray-500">
                    {new Date(
                      invoice.time || invoice.createdAt || Date.now(),
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>

                  <button
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    onClick={() =>
                      window.open(
                        Array.isArray(invoice.imageUrl)
                          ? invoice.imageUrl[0]
                          : invoice.imageUrl,
                        "_blank",
                      )
                    }
                  >
                    View Details →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default Record;
